import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, EmptyState, Picker, Skeleton } from "@cntrl/ui";
import { useBandContext } from "../../app/bandContext.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { useReleases } from "../../app/hooks/useReleases.js";
import { ConfirmDialog } from "../shared/ConfirmDialog.js";
import {
  useAdsCampaigns,
  useAdsInsights,
  useControlCampaign,
  useTagCampaign,
  type AdCampaignView,
  type AdsApiStatus,
  type AdsKpiWindow,
  type AudienceMarket,
} from "../../app/hooks/useAds.js";
import {
  campaignCountLine,
  compactOrDash,
  costPerClick,
  ctr,
  flightDates,
  moneyOrDash,
  pctOrDash,
} from "./marketingFormat.js";
import styles from "./AdsManager.module.scss";

/** The visible API-status strip — a missing ads_management grant / disconnected Meta / vendor outage renders an
 * explained CTA, never a silent empty table.*/
function StatusStrip({
  status,
  onConnect,
  onRetry,
}: {
  status: AdsApiStatus;
  onConnect: (() => void) | null;
  onRetry: () => void;
}) {
  if (status.state === "not-connected")
    return (
      <div className={styles.status} role="status">
        <span>
          <strong>Connect Meta</strong> to manage your ad campaigns and see paid performance.
        </span>
        {onConnect ? (
          <Button variant="accent" size="sm" onClick={onConnect}>
            Connect Meta
          </Button>
        ) : null}
      </div>
    );
  if (status.state === "ads-permission-missing")
    return (
      <div className={styles.status} role="status">
        <span>
          <strong>Ads access isn&apos;t granted.</strong> Reconnect Meta with ad permissions to manage campaigns.
        </span>
        {onConnect ? (
          <Button variant="accent" size="sm" onClick={onConnect}>
            Grant ads access
          </Button>
        ) : null}
      </div>
    );
  return (
    <div className={styles.status} role="status">
      <span>Meta is temporarily unavailable. Your campaigns couldn&apos;t load.</span>
      <Button variant="ghost" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

/** The status text — plaintext, not a chip (Ryan). The bucket is the SERVER's `state` — active / paused /
 * ended / draft — not a relabelling of Meta's `effective_status`: an ACTIVE campaign whose flight has ended is
 * `ended` here, which is the whole point of the `campaign-ended` fix. */
function StatusText({ state }: { state: string }) {
  return <>{state.charAt(0).toUpperCase() + state.slice(1)}</>;
}

/**
 * The account KPI strip: Spend · Reach · CTR · Cost per click, each with a sub-line naming the window.
 *
 * **"Cost per click", not the spec's "Cost · stream".** The reference's own worker computes that tile as
 * `spend / link_clicks` and its comment says so outright ("Heuristic: link clicks as a stream proxy. Refine
 * once we wire Spotify pixel conversions"), so the label names a stream while the number counts a click — the
 * `streams_28d / 4` failure, an estimate presented as a measurement. Legacy's own React port had already
 * corrected the label to "Cost per click"; we follow the port. When a real stream attribution lands, the tile
 * can take the spec's label and mean it.
 */
function KpiStrip({ current, connected }: { current: AdsKpiWindow | null | undefined; connected: boolean }) {
  const sub = connected ? "last 30d" : "Not connected";
  // `spend` absent ⇒ this seat holds no marketing money, so the two money tiles are DROPPED rather than dashed.
  // Present with a null figure ⇒ entitled and we hold nothing, which is what the dash means. Reach and CTR are
  // not money and always render — a coordinator without money still needs to know whether the ads are landing.
  const spend = current?.spend;
  const rate = ctr(current?.clicks, current?.impressions);
  return (
    <div className={styles.kpis}>
      {spend !== undefined && (
        <Kpi label="Spend" value={moneyOrDash(spend?.spendCents)} sub={sub} />
      )}
      <Kpi label="Reach" value={compactOrDash(current?.reach)} sub={sub} />
      <Kpi label="CTR" value={pctOrDash(rate)} sub={sub} />
      {spend !== undefined && (
        <Kpi label="Cost per click" value={moneyOrDash(costPerClick(spend?.spendCents, current?.clicks))} sub={sub} />
      )}
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className={styles.kpi}>
      <p className={styles.kpiLabel}>{label}</p>
      <p className={styles.kpiValue}>{value}</p>
      <span className={styles.kpiSub}>{sub}</span>
    </div>
  );
}

/** The panel's own height budget — the flip-up threshold, and what its list is capped to. */
const TAG_PICKER_MAX_H = 320;

/**
 * The release-tag picker. Newest-first with a search box, plus "Remove tag" when one is set.
 *
 * The empty copy points at the release surface rather than saying "none", because a band with no releases has
 * something to do about it.
 *
 * **It is PORTALED to the body and positioned `fixed` from its trigger's rect**, which it did not need to be
 * while the campaigns table grew without limit. The table is capped at six rows and scrolls now, and a scroll
 * container CLIPS an absolutely positioned descendant — so anchored in its own cell this panel would open as
 * a sliver on exactly the rows nearest the fold. Re-measured on capture-phase scroll so it tracks the row as
 * the table moves under it; that is the `Picker` listbox's arrangement, for the same reason.
 */
function TagPicker({
  anchorTo,
  current,
  onPick,
  onClose,
}: {
  /** The trigger the panel hangs off. Its rect is the only thing positioning this. */
  anchorTo: RefObject<HTMLButtonElement | null>;
  current: string | null;
  onPick: (releaseId: string | null) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const releases = useReleases({});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // Measured on open and on any scroll/resize — `capture` is what catches the table's own scroller, which is
  // the one that moves this panel's row. Flips upward when the space below is tighter than the panel is tall.
  const [anchor, setAnchor] = useState<{ top: number; right: number; up: boolean } | null>(null);
  useEffect(() => {
    const measure = () => {
      const el = anchorTo.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const up = spaceBelow < TAG_PICKER_MAX_H && r.top > spaceBelow;
      setAnchor({ top: up ? r.top - 4 : r.bottom + 4, right: window.innerWidth - r.right, up });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [anchorTo]);

  const sorted = useMemo(() => {
    const items = [...(releases.data?.items ?? [])];
    items.sort((a, b) => {
      const da = a.releaseDate ? Date.parse(`${a.releaseDate}T12:00:00`) : 0;
      const db = b.releaseDate ? Date.parse(`${b.releaseDate}T12:00:00`) : 0;
      return db !== da ? db - da : (a.title ?? "").localeCompare(b.title ?? "");
    });
    return items;
  }, [releases.data]);

  const needle = q.trim().toLowerCase();
  const shown = needle ? sorted.filter((r) => (r.title ?? "").toLowerCase().includes(needle)) : sorted;

  if (!anchor) return null;

  return createPortal(
    <div
      className={styles.tagPicker}
      style={anchor.up ? { bottom: window.innerHeight - anchor.top, right: anchor.right } : { top: anchor.top, right: anchor.right }}
      // The panel is no longer a DOM child of its cell, so a press inside it would read as an outside press
      // to any ancestor dismiss handler — and the search box must keep focus while it is typed in.
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className={styles.tagPickerHead}>Tag campaign to release</div>
      <input
        type="text"
        className={styles.tagPickerSearch}
        placeholder="Search releases…"
        value={q}
        autoComplete="off"
        aria-label="Search releases"
        onChange={(e) => setQ(e.target.value)}
      />
      <div className={styles.tagPickerList}>
        {shown.length === 0 ? (
          <div className={styles.tagPickerEmpty}>
            {sorted.length === 0 ? "No releases yet. Add a release on the Releases tab first." : "No matching releases."}
          </div>
        ) : (
          shown.map((r) => (
            <button
              key={r.releaseId}
              type="button"
              className={`${styles.tagPickerItem} ${r.releaseId === current ? styles.tagPickerItemActive : ""}`}
              onClick={() => onPick(r.releaseId ?? null)}
            >
              <span className={styles.tagPickerName}>{r.title || "Untitled release"}</span>
              {r.releaseDate ? (
                <span className={styles.tagPickerDate}>
                  {new Date(`${r.releaseDate}T12:00:00`).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              ) : null}
            </button>
          ))
        )}
      </div>
      {current ? (
        <button type="button" className={styles.tagPickerUntag} onClick={() => onPick(null)}>
          Remove tag
        </button>
      ) : null}
    </div>,
    document.body,
  );
}

function CampaignRow({
  c,
  releaseTitles,
  disabled,
  onControl,
  onTag,
  onClose,
}: {
  c: AdCampaignView;
  releaseTitles: Map<string, string>;
  disabled: boolean;
  onControl: (id: string, action: string, dailyBudgetCents?: number) => void;
  onTag: (id: string, releaseId: string | null) => void;
  onClose: (c: AdCampaignView) => void;
}) {
  const [picking, setPicking] = useState(false);
  const tagBtnRef = useRef<HTMLButtonElement>(null);
  const spend = c.spend;
  const id = c.id ?? "";
  const name = c.name || id;
  const state = c.state ?? "draft";
  const paused = state === "paused";
  const releaseName = c.releaseId ? (releaseTitles.get(c.releaseId) ?? "Tagged release") : null;

  return (
    <tr>
      {/* The flex layout lives on a wrapper, never on the `<td>` — a cell whose `display` is not `table-cell`
          is taken OUT of the row and wrapped in an anonymous one, which aligns on its own baseline and draws
          none of `.table td`'s border. That is what put this column's contents on a different line from their
          own row and left the Campaign and Manage columns with no row rule under them. */}
      <td className={styles.cName}>
        <span className={styles.nameWrap}>
          <span className={styles.thumb} aria-hidden="true">
            {c.thumbnailUrl ? (
              <img className={styles.thumbImg} src={c.thumbnailUrl} alt="" loading="lazy" />
            ) : (
              <span className={styles.thumbLetter}>{name.charAt(0).toUpperCase()}</span>
            )}
          </span>
          <span className={styles.nameCol}>
            <span className={styles.campaignName}>{name}</span>
            <span className={styles.campaignDates}>{flightDates(c.startTime, c.stopTime)}</span>
          </span>
        </span>
      </td>
      <td>
        <StatusText state={state} />
      </td>
      {/* The money block is absent for a seat without marketing money, so the Spend cell — and its header — go
          with it. A header over an empty column says "we hold no spend", which is a different sentence from
          "you may not see spend". */}
      {spend !== undefined && <td className={styles.cNum}>{moneyOrDash(spend?.spendCents)}</td>}
      <td className={styles.cNum}>{compactOrDash(c.reach)}</td>
      <td className={styles.cNum}>{pctOrDash(ctr(c.clicks, c.impressions))}</td>
      {/* "Results" is link CLICKS — Meta's insights carry no dedicated results field. 0 is a legitimate value
          and renders as such; only a genuinely absent metric dashes. */}
      <td className={styles.cNum}>{c.clicks != null ? c.clicks.toLocaleString() : "-"}</td>
      <td className={styles.cRelease}>
        <button
          ref={tagBtnRef}
          type="button"
          className={`${styles.relTag} ${releaseName ? "" : styles.relTagEmpty}`}
          title={releaseName ? "Change or remove this tag" : "Tag this campaign to a release"}
          onClick={() => setPicking((p) => !p)}
        >
          {releaseName ? releaseName : "+ Tag"}
        </button>
        {picking ? (
          <TagPicker
            anchorTo={tagBtnRef}
            current={c.releaseId ?? null}
            onPick={(releaseId) => {
              setPicking(false);
              onTag(id, releaseId);
            }}
            onClose={() => setPicking(false)}
          />
        ) : null}
      </td>
      {/* Same rule as the name cell above: the `<td>` stays a table-cell and the flex row is a wrapper inside
          it. `justify-content: flex-end` keeps the three controls hard against the table's trailing edge, so
          a row with no Pause (ended / draft) lines its Close up with every other row's rather than sliding
          left into the gap the missing button leaves. */}
      <td className={styles.cActions}>
        <span className={styles.actionsRow}>
          {/* `campaign-ended` (2026-07-06): an ended campaign gets NO pause/resume, like a draft — its flight is
              over, so resuming it is not a thing that can happen. */}
          {state !== "ended" && state !== "draft" ? (
            <Button size="sm" onClick={() => onControl(id, paused ? "resume" : "pause")} disabled={disabled}>
              {paused ? "Resume" : "Pause"}
            </Button>
          ) : null}
          {spend !== undefined ? (
            <BudgetButton c={c} disabled={disabled} onControl={onControl} />
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => onClose(c)} disabled={disabled}>
            Close
          </Button>
        </span>
      </td>
    </tr>
  );
}

/** The inline daily-budget editor. A number input rather than the reference's `prompt()` — but it keeps that
 *  path's MINIMUM: Meta rejects a daily budget under 1.00, so a smaller figure is a guaranteed vendor error on
 *  a control the user just used. The server holds the same floor. */
function BudgetButton({
  c,
  disabled,
  onControl,
}: {
  c: AdCampaignView;
  disabled: boolean;
  onControl: (id: string, action: string, dailyBudgetCents?: number) => void;
}) {
  const current = c.spend?.dailyBudgetCents;
  const [budget, setBudget] = useState(current != null ? (current / 100).toFixed(2) : "");
  const save = () => {
    const cents = Math.round(parseFloat(budget) * 100);
    if (!Number.isFinite(cents) || cents < 100 || cents === current) return;
    onControl(c.id ?? "", "budget", cents);
  };
  return (
    <input
      className={styles.budgetInput}
      type="number"
      min="1"
      step="0.01"
      inputMode="decimal"
      aria-label={`Daily budget for ${c.name || c.id} (USD, minimum 1.00)`}
      value={budget}
      onChange={(e) => setBudget(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => e.key === "Enter" && save()}
      disabled={disabled}
    />
  );
}

/** A click count for the fan-markets table. NULL ⇒ no click data for that market ⇒ an em dash; a real 0 (Meta
 *  answered and nobody clicked there) prints "0" — the two are different sentences and must never share a glyph. */
function clicksCell(n: number | null | undefined): string {
  if (n === null || n === undefined) return "\u2014";
  return compactOrDash(n);
}

/**
 * "Push to where your fans live" (ART-288) — the band's top cities as COLUMNS, sorted by fans left to right, with
 * two rows: Fans (Spotify listeners + Instagram followers) and Ad clicks (30d) (Meta, last 30 days). The city →
 * market join is the SERVER's (`audienceMarkets[].adClicks`), so this renders a verdict rather than computing one:
 * a number (0 included) when Meta answered, null when we hold no click data.
 *
 * The table is wider than the card on most screens, so it scrolls sideways INSIDE the card with the row labels
 * pinned on the left; the page itself never scrolls horizontally.
 */
function AudienceBuilder({ markets }: { markets: AudienceMarket[] }) {
  // The server already orders by fans; sort again so the column order is this component's stated contract.
  const cols = [...markets].sort((a, b) => (b.fans ?? 0) - (a.fans ?? 0));
  return (
    <div className={styles.audience}>
      {/* "Audience builder" head + its note were killed (ART-205, Ryan); the card itself stays. */}
      <article className={styles.audienceCard}>
        <p className={styles.audienceCardTitle}>Push to where your fans live</p>
        {cols.length === 0 ? (
          // A card that vanishes is indistinguishable from a card nobody built — so an empty audience SAYS it
          // is empty, and says what to do about it.
          <p className={styles.audienceEmpty}>No top cities yet, finish the Connect step on the Dashboard.</p>
        ) : (
          <div className={styles.marketsWrap} role="region" aria-label="Top cities by fans">
            <table className={styles.marketsTable}>
              <thead>
                <tr>
                  <td className={styles.marketsCorner} />
                  {cols.map((m) => (
                    <th key={m.label ?? ""} scope="col">
                      {m.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Fans</th>
                  {cols.map((m) => (
                    <td key={m.label ?? ""}>{compactOrDash(m.fans)}</td>
                  ))}
                </tr>
                <tr>
                  <th scope="row">Ad clicks (30d)</th>
                  {cols.map((m) => (
                    <td
                      key={m.label ?? ""}
                      title={m.adClicks == null ? "No Meta click data for this market" : undefined}
                    >
                      {clicksCell(m.adClicks)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </article>
    </div>
  );
}

/**
 * The Meta Ads section — the page's centrepiece: toolbar, account KPI strip, campaigns table, audience builder.
 *
 * It renders INLINE, as the reference does. It used to sit behind a "Manage campaigns" toggle because ads reads
 * reach Meta synchronously (unlike the composed, projection-only overview beside it) — but that hid the KPI
 * strip, the table AND the audience builder, i.e. everything the page is for, and it made every one of the
 * reference's carefully-distinct empty states unreachable. Two GETs per page load is fewer than the reference's
 * five-per-account, they are deduped by the query cache's `staleTime`, and the section is the reason a band
 * opened this page.
 */
export function AdsManager({
  onConnect,
  onNewCampaign,
}: {
  onConnect: (() => void) | null;
  onNewCampaign: (() => void) | null;
}) {
  // NOTE there is deliberately no `metaConnected` prop. "Connected" HERE means what the reference means by it —
  // the accounts read returned at least one AD ACCOUNT — which is a stricter fact than the connection row the
  // hero reads, and the one every empty state in this section turns on. A band can hold a live Meta connection
  // with no ad account behind it, and this section has to say so.
  const [account, setAccount] = useState<string | null>(null);
  const [draftsOnly, setDraftsOnly] = useState(false);
  const [closing, setClosing] = useState<AdCampaignView | null>(null);
  const qc = useQueryClient();
  const { bandId } = useBandContext();

  const campaigns = useAdsCampaigns(account, true);
  const insights = useAdsInsights(account, true);
  const control = useControlCampaign();
  const tag = useTagCampaign();
  const releases = useReleases({});

  // When the insights refresh lands a real account KPI snapshot, repaint the overview tiles above (same page) —
  // the on-demand landing → BuildKpisAsync seam becomes visible without a page reload. Gated on a real ('ok') read.
  useEffect(() => {
    if (insights.isSuccess && insights.data?.apiStatus?.state === "ok")
      void qc.invalidateQueries({ queryKey: ["band", bandId, "marketing-overview"] });
  }, [insights.isSuccess, insights.data?.apiStatus?.state, insights.dataUpdatedAt, qc, bandId]);

  const onControl = (id: string, action: string, dailyBudgetCents?: number) =>
    control.mutate({ id, action, dailyBudgetCents });

  const data = campaigns.data;
  const status = data?.apiStatus;
  const accounts = data?.adAccounts ?? [];
  const allRows = data?.campaigns ?? [];
  const rows = draftsOnly ? allRows.filter((c) => c.state === "draft") : allRows;
  const connected = status?.state === "ok" && accounts.length > 0;

  const releaseTitles = useMemo(
    () => new Map((releases.data?.items ?? []).map((r) => [r.releaseId ?? "", r.title ?? "Untitled release"])),
    [releases.data],
  );

  // The open guard, the reference's: the creator can only spend money against a connected account, so with none
  // the control is DISABLED rather than opening a form whose only outcome is a refusal (the dead-button rule —
  // legacy alerted instead, which is the same refusal delivered later and louder).
  const canCreate = connected && onNewCampaign != null;

  return (
    <section className={styles.section} aria-labelledby="ads-heading">
      <header className={styles.head}>
        <div>
          <h2 id="ads-heading" className={styles.title}>
            Meta Ads
          </h2>
        </div>
        <div className={styles.toolbar}>
          <Picker
            label="Ad account"
            bare
            triggerClassName={styles.accountTrigger}
            value={account ?? data?.selectedAdAccountId ?? ""}
            onChange={(next) => setAccount(next || null)}
            options={
              accounts.length > 0
                ? accounts.map((a) => ({ value: a.id ?? "", label: `${a.name} (${a.currency})` }))
                : [{ value: "", label: "Connect ad account…" }]
            }
            disabled={accounts.length === 0}
          />
          <Button
            size="sm"
            variant="ghost"
            aria-pressed={draftsOnly}
            className={draftsOnly ? styles.filterOn : undefined}
            onClick={() => setDraftsOnly((v) => !v)}
          >
            Drafts{data?.counts ? ` (${data.counts.draft ?? 0})` : ""}
          </Button>
          {/* `Drafts (n)` beside this counts META campaigns in the server's catch-all bucket (DRAFT /
              IN_PROCESS / WITH_ISSUES / PENDING_REVIEW) — the same word the row's own `Draft` pill uses — and
              it is the ONLY thing on this page called drafts. The Ad Creator's own saved-form drafts went on
              2026-09-09: they had no legacy counterpart, and their count read a different population from this
              one, which is what made the two disagree in front of a reader. */}
          <Button
            size="sm"
            variant="accent"
            disabled={!canCreate}
            title={canCreate ? undefined : "Connect Meta and pick an ad account before creating a campaign."}
            onClick={() => onNewCampaign?.()}
          >
            + New campaign
          </Button>
        </div>
      </header>

      {campaigns.isLoading ? (
        <div className={styles.skeletons} role="status" aria-live="polite" aria-label="Loading">
          <Skeleton variant="tile" />
          <Skeleton variant="line" width="70%" />
          <Skeleton variant="line" width="45%" />
        </div>
      ) : campaigns.isError ? (
        <EmptyState
          title="Couldn't load campaigns"
          description={campaigns.error instanceof ApiError ? campaigns.error.message : "Please try again."}
          action={<Button onClick={() => void campaigns.refetch()}>Retry</Button>}
        />
      ) : (
        <>
          {status && status.state !== "ok" ? (
            <StatusStrip status={status} onConnect={onConnect} onRetry={() => void campaigns.refetch()} />
          ) : null}

          <KpiStrip current={insights.data?.current} connected={connected} />

          <div className={styles.campaignsCard}>
            <header className={styles.campaignsHead}>
              <h4 className={styles.cardTitle}>Campaigns</h4>
              {/* Not connected keeps the reference's placeholder line verbatim, so the card does not visibly
                  grow a row the moment a band connects. */}
              <p className={styles.countLine}>{campaignCountLine(connected ? data?.counts : null)}</p>
            </header>
            {rows.length === 0 ? (
              <div className={styles.campaignsEmpty}>
                {draftsOnly
                  ? "No draft campaigns."
                  : "Connect Meta in Settings → Connections to see your Facebook + Instagram ads here."}
              </div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.cName}>Campaign</th>
                      <th>Status</th>
                      {rows[0]?.spend !== undefined && <th className={styles.cNum}>Spend</th>}
                      <th className={styles.cNum}>Reach</th>
                      <th className={styles.cNum}>CTR</th>
                      <th className={styles.cNum}>Results</th>
                      <th>Release</th>
                      <th className={styles.cActions}>Manage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => (
                      <CampaignRow
                        key={c.id ?? ""}
                        c={c}
                        releaseTitles={releaseTitles}
                        disabled={control.isPending}
                        onControl={onControl}
                        onTag={(id, releaseId) => tag.mutate({ id, releaseId })}
                        onClose={setClosing}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <AudienceBuilder markets={data?.audienceMarkets ?? []} />
        </>
      )}

      {closing ? (
        <ConfirmDialog
          title="Close this campaign?"
          description={`This ends "${closing.name || closing.id || "this campaign"}" on Meta. Its spend so far stays on the release budget.`}
          confirmLabel="Close campaign"
          loading={control.isPending}
          onConfirm={() => {
            onControl(closing.id ?? "", "close");
            setClosing(null);
          }}
          onCancel={() => setClosing(null)}
        />
      ) : null}
    </section>
  );
}
